import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { contactsApi } from '../api/contacts-api';
import {
  UnifiedContact,
  ContactsFilter,
  ContactsListResponse,
  ContactType,
  CreateContactPayload,
  UpdateContactPayload,
} from '../types/contacts-types';

export const fetchContacts = createAsyncThunk(
  'contacts/fetchContacts',
  async (filters: ContactsFilter | undefined, { rejectWithValue }) => {
    try {
      return await contactsApi.getAll(filters);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar contactos',
      );
    }
  },
);

export const fetchContactDetail = createAsyncThunk(
  'contacts/fetchContactDetail',
  async (
    { type, id }: { type: ContactType; id: string },
    { rejectWithValue },
  ) => {
    try {
      return await contactsApi.getById(type, id);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar contacto',
      );
    }
  },
);

export const fetchContactStats = createAsyncThunk(
  'contacts/fetchContactStats',
  async (_, { rejectWithValue }) => {
    try {
      return await contactsApi.getStats();
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al cargar estadisticas',
      );
    }
  },
);

export const createContact = createAsyncThunk(
  'contacts/createContact',
  async (payload: CreateContactPayload, { rejectWithValue }) => {
    try {
      return await contactsApi.create(payload);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al crear contacto',
      );
    }
  },
);

export const updateContact = createAsyncThunk(
  'contacts/updateContact',
  async (
    { type, id, payload }: { type: ContactType; id: string; payload: UpdateContactPayload },
    { rejectWithValue },
  ) => {
    try {
      return await contactsApi.update(type, id, payload);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al actualizar contacto',
      );
    }
  },
);

export const deleteContact = createAsyncThunk(
  'contacts/deleteContact',
  async (
    { type, id }: { type: ContactType; id: string },
    { rejectWithValue },
  ) => {
    try {
      await contactsApi.delete(type, id);
      return { type, id };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || 'Error al eliminar contacto',
      );
    }
  },
);

interface ContactsState {
  contacts: UnifiedContact[];
  currentContact: UnifiedContact | null;
  filters: ContactsFilter;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  countByType: Record<string, number>;
  stats: Record<string, number>;
  loading: boolean;
  loadingDetail: boolean;
  loadingStats: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: ContactsState = {
  contacts: [],
  currentContact: null,
  filters: { page: 1, limit: 20 },
  total: 0,
  page: 1,
  limit: 20,
  totalPages: 0,
  countByType: {},
  stats: {},
  loading: false,
  loadingDetail: false,
  loadingStats: false,
  saving: false,
  error: null,
};

const contactsSlice = createSlice({
  name: 'contacts',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<ContactsFilter>) => {
      state.filters = action.payload;
    },
    clearCurrentContact: (state) => {
      state.currentContact = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchContacts
      .addCase(fetchContacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchContacts.fulfilled,
        (state, action: PayloadAction<ContactsListResponse>) => {
          state.loading = false;
          state.contacts = action.payload.contacts;
          state.total = action.payload.total;
          state.page = action.payload.page;
          state.limit = action.payload.limit;
          state.totalPages = action.payload.totalPages;
          state.countByType = action.payload.countByType;
        },
      )
      .addCase(fetchContacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // fetchContactDetail
      .addCase(fetchContactDetail.pending, (state) => {
        state.loadingDetail = true;
        state.error = null;
      })
      .addCase(
        fetchContactDetail.fulfilled,
        (state, action: PayloadAction<UnifiedContact>) => {
          state.loadingDetail = false;
          state.currentContact = action.payload;
        },
      )
      .addCase(fetchContactDetail.rejected, (state, action) => {
        state.loadingDetail = false;
        state.error = action.payload as string;
      })
      // fetchContactStats
      .addCase(fetchContactStats.pending, (state) => {
        state.loadingStats = true;
      })
      .addCase(
        fetchContactStats.fulfilled,
        (state, action: PayloadAction<Record<string, number>>) => {
          state.loadingStats = false;
          state.stats = action.payload;
        },
      )
      .addCase(fetchContactStats.rejected, (state, action) => {
        state.loadingStats = false;
        state.error = action.payload as string;
      })
      // createContact
      .addCase(createContact.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createContact.fulfilled, (state, action: PayloadAction<UnifiedContact>) => {
        state.saving = false;
        state.contacts.unshift(action.payload);
        state.total += 1;
      })
      .addCase(createContact.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      })
      // updateContact
      .addCase(updateContact.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(updateContact.fulfilled, (state, action: PayloadAction<UnifiedContact>) => {
        state.saving = false;
        const idx = state.contacts.findIndex(
          (c) => c.id === action.payload.id && c.type === action.payload.type,
        );
        if (idx !== -1) {
          state.contacts[idx] = action.payload;
        }
      })
      .addCase(updateContact.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      })
      // deleteContact
      .addCase(deleteContact.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteContact.fulfilled, (state, action) => {
        state.saving = false;
        const { type, id } = action.payload;
        state.contacts = state.contacts.filter(
          (c) => !(c.id === id && c.type === type),
        );
        state.total = Math.max(0, state.total - 1);
      })
      .addCase(deleteContact.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, clearCurrentContact, clearError } =
  contactsSlice.actions;
export default contactsSlice.reducer;
